/**
 * Present-mode fit-to-screen math (UX review finding: a two-tile dashboard
 * huddled in the top-left corner of a fullscreen display at desktop text size,
 * unreadable from across a room).
 *
 * Model: the grid is laid out on a FIXED desktop-ish canvas
 * (`PRESENT_DESIGN_GRID_WIDTH`) and the USED bounding box of that composition
 * is then scaled UNIFORMLY to fill the display. Uniform on purpose — tiles keep
 * the proportions they were designed at (a table keeps its column widths, a
 * chart its tick density); text simply gets bigger. Nothing re-flows, so the
 * presented dashboard is the same composition the editor showed, just larger.
 *
 * Pure: the viewport box is an input (no DOM, no React), so every case here is
 * unit-testable.
 */
import { GRID_COLS } from '../utils/gridLayout';
import { GRID_GAP, GRID_ROW_HEIGHT } from '../hooks/useGridInteractions';
import type { TileLayout } from '../types/dashboardTypes';

/**
 * Canvas the 12-column grid is laid out on before scaling. A desktop-ish width
 * so tile internals (table columns, chart axes, wrapped labels) resolve exactly
 * as they do in the normal tab — magnifying a cramped 400px layout would make
 * big-but-squashed tiles instead of readable ones. Narrower viewports lay out
 * at their own width (never wider than the screen, which would clip).
 */
export const PRESENT_DESIGN_GRID_WIDTH = 960;

/** Upper bound on magnification — protects a lone stat tile from absurdity. */
export const PRESENT_MAX_SCALE = 4;

/** `DashboardGrid`'s own `p-4` padding, which sits inside the scaled wrapper. */
export const PRESENT_GRID_PADDING = 16;

export type PresentFitInput = {
  layouts: readonly TileLayout[];
  viewportWidth: number;
  viewportHeight: number;
};

export type PresentFit = {
  /** Width (px) to give the wrapper the grid lays out in (padding included). */
  wrapperWidth: number;
  /** Uniform scale factor, always > 1 (a fit that gains nothing returns null). */
  scale: number;
  /** Pre-scale translation pulling the used bounding box's top-left to origin. */
  offsetX: number;
  offsetY: number;
  /** Post-scale size of the used bounding box — the caller centers with these. */
  scaledWidth: number;
  scaledHeight: number;
};

/**
 * Returns the transform that makes the composition fill `viewport`, or `null`
 * when fitting doesn't apply — no tiles, an unmeasured viewport, a dashboard
 * TALLER than the screen (kept scrolling at desktop size rather than shrunk
 * below readable), or a scale that wouldn't magnify at all. `null` means
 * "render the plain grid exactly as before".
 */
export function presentFit({
  layouts,
  viewportWidth,
  viewportHeight,
}: PresentFitInput): PresentFit | null {
  if (layouts.length === 0 || viewportWidth <= 0 || viewportHeight <= 0) return null;

  const minX = Math.min(...layouts.map((l) => l.x));
  const minY = Math.min(...layouts.map((l) => l.y));
  const usedCols = Math.min(GRID_COLS, Math.max(1, Math.max(...layouts.map((l) => l.x + l.w)) - minX));
  const usedRows = Math.max(1, Math.max(...layouts.map((l) => l.y + l.h)) - minY);

  // Row height is fixed px, so content height is independent of the canvas
  // width — a dashboard that doesn't fit vertically can't be fitted at all.
  const contentHeight = usedRows * GRID_ROW_HEIGHT + (usedRows - 1) * GRID_GAP;
  if (contentHeight > viewportHeight) return null;

  const gridWidth = Math.min(viewportWidth, PRESENT_DESIGN_GRID_WIDTH);
  const colWidth = (gridWidth - GRID_GAP * (GRID_COLS - 1)) / GRID_COLS;
  if (colWidth <= 0) return null;
  const contentWidth = usedCols * colWidth + (usedCols - 1) * GRID_GAP;

  const scale = Math.min(
    viewportWidth / contentWidth,
    viewportHeight / contentHeight,
    PRESENT_MAX_SCALE,
  );
  if (scale <= 1) return null;

  return {
    wrapperWidth: gridWidth + PRESENT_GRID_PADDING * 2,
    scale,
    offsetX: PRESENT_GRID_PADDING + minX * (colWidth + GRID_GAP),
    offsetY: PRESENT_GRID_PADDING + minY * (GRID_ROW_HEIGHT + GRID_GAP),
    scaledWidth: contentWidth * scale,
    scaledHeight: contentHeight * scale,
  };
}
